import React, { useMemo, useState, useCallback } from 'react';
import { Box, Typography, Checkbox, Chip, Tooltip } from '@mui/material';
import type { TargetTemplateInfo, EditedConfigInfo } from './types';
import type { Planner } from './usePlanner';

type Tab = 'configs' | 'strings';

function stripHtml(s: string): string {
  return (s || '').replace(/<[^>]*>/g, '').trim();
}

export const DiffViewer: React.FC<{
  targets: TargetTemplateInfo[];
  editedConfigs: EditedConfigInfo[];
  planner: Planner;
}> = ({ targets, editedConfigs, planner }) => {
  const [tab, setTab] = useState<Tab>('configs');
  const target = targets.find(t => t.templateKey === planner.activeTargetKey);

  const editedByKey = useMemo(() => {
    const m = new Map<string, EditedConfigInfo>();
    for (const c of editedConfigs) m.set(c.configKey, c);
    return m;
  }, [editedConfigs]);

  const applicable = target?.applicableConfigKeys ?? [];
  const hydrated = applicable
    .map(k => editedByKey.get(k))
    .filter((c): c is EditedConfigInfo => !!c);

  const filtered = hydrated.filter(c =>
    tab === 'strings' ? c.fieldType === 'string' : c.fieldType !== 'string',
  );

  const sel = planner.selection.get(target?.templateKey ?? '') ?? new Set<string>();

  const configCount = hydrated.filter(c => c.fieldType !== 'string').length;
  const stringCount = hydrated.filter(c => c.fieldType === 'string').length;

  const selectedInTab = filtered.filter(c => sel.has(c.configKey)).length;
  const allTabSelected = filtered.length > 0 && selectedInTab === filtered.length;
  const someTabSelected = selectedInTab > 0 && selectedInTab < filtered.length;

  const selectAllTab = useCallback(() => {
    if (!target) return;
    filtered.forEach(c => {
      if (!sel.has(c.configKey)) planner.toggleConfig(target.templateKey, c.configKey);
    });
  }, [filtered, sel, planner, target]);

  const clearTab = useCallback(() => {
    if (!target) return;
    filtered.forEach(c => {
      if (sel.has(c.configKey)) planner.toggleConfig(target.templateKey, c.configKey);
    });
  }, [filtered, sel, planner, target]);

  if (!target) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          color: 'text.secondary',
          fontSize: '0.85rem',
        }}
      >
        Select a target to review the diff.
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: 'grey.50' }}>
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          px: 3,
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.25 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#003366' }}>
              {target.displayName}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontFamily: 'monospace', color: 'text.disabled', fontSize: '0.65rem' }}
            >
              {target.templateKey}
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: 'rgba(0,127,128,0.08)',
              color: '#007f80',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.65rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              ml: 1,
            }}
          >
            {sel.size} changes selected
          </Box>
        </Box>

        {/* Tab switcher */}
        <Box
          sx={{
            display: 'inline-flex',
            gap: '4px',
            p: '4px',
            bgcolor: '#f1f5f9',
            borderRadius: '6px',
          }}
        >
          {(['configs', 'strings'] as Tab[]).map(t => {
            const count = t === 'configs' ? configCount : stringCount;
            const active = tab === t;
            return (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 28,
                  paddingLeft: 12,
                  paddingRight: 12,
                  borderRadius: 4,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  background: active ? 'white' : 'transparent',
                  color: active ? '#003366' : '#64748b',
                  boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.12s',
                }}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
                <span
                  style={{
                    background: active ? '#e2e8f0' : 'transparent',
                    color: '#64748b',
                    borderRadius: 4,
                    padding: '1px 5px',
                    fontSize: '0.6rem',
                    fontWeight: 700,
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ minHeight: 0, flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
        {filtered.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No {tab} apply to this target.
          </Typography>
        ) : (
          <>
            {/* Select all / Clear row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5, mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                  size="small"
                  checked={allTabSelected}
                  indeterminate={someTabSelected}
                  onChange={() => allTabSelected ? clearTab() : selectAllTab()}
                  sx={{ p: 0.25 }}
                />
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                  Select all {tab} ({selectedInTab}/{filtered.length})
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <button
                  onClick={selectAllTab}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#007f80', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}
                >
                  Select all
                </button>
                <span style={{ color: '#d1d5db' }}>|</span>
                <button
                  onClick={clearTab}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}
                >
                  Clear
                </button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {filtered.map(cfg => (
                <DiffRow
                  key={cfg.configKey}
                  cfg={cfg}
                  target={target}
                  planner={planner}
                  selected={sel.has(cfg.configKey)}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

function DiffRow({
  cfg,
  target,
  planner,
  selected,
}: {
  cfg: EditedConfigInfo;
  target: TargetTemplateInfo;
  planner: Planner;
  selected: boolean;
}) {
  const sourceVal = cfg.newValue;
  const currentVal = target.values?.[cfg.configKey];
  const hasCurrent = currentVal !== undefined && currentVal !== '';
  const copiedEntry = cfg.copiedToTemplates?.find(ct => ct.toTemplateKey === target.templateKey);
  const wasCopied = !!copiedEntry;
  // isCopyInSync must be derived before isOverride: when the entry is in sync,
  // target.values[k] holds the pre-copy originalValue (not the live target value),
  // so comparing it against sourceVal would produce a false Override badge.
  const isCopyInSync = wasCopied && (copiedEntry!.isInSync === true || !hasCurrent);
  const isOverride = hasCurrent && currentVal !== sourceVal && !isCopyInSync;

  const beforeDisplay = hasCurrent ? stripHtml(currentVal!) : '(empty)';
  const afterDisplay = sourceVal ? stripHtml(sourceVal) : '(empty)';

  return (
    <Box
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: isOverride ? 'warning.light' : 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* Row header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {/* Left: checkbox + display name */}
        <Checkbox
          size="small"
          checked={selected}
          onChange={() => planner.toggleConfig(target.templateKey, cfg.configKey)}
          sx={{ p: 0.25, flexShrink: 0 }}
        />
        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#3B3B3B', flexShrink: 0 }}>
          {cfg.displayName}
        </Typography>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Right: chips + config key */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, minWidth: 0 }}>
          {isOverride && (
            <Chip label="Override" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
          )}
          {isCopyInSync && (
            <Chip label="All sync" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
          )}
          {wasCopied && !isCopyInSync && (
            <Chip label="Copied" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', color: 'text.secondary', borderColor: 'divider' }} />
          )}
          <Tooltip title={cfg.configKey} placement="top">
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                color: 'text.disabled',
                fontSize: '0.65rem',
                maxWidth: 220,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'right',
                cursor: 'default',
              }}
            >
              {cfg.configKey}
            </Typography>
          </Tooltip>
        </Box>
      </Box>

      {/* Before / After */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
        {/* Before */}
        <Box
          sx={{
            bgcolor: '#FEF2F2',
            px: 1.5,
            py: 1,
            borderRight: 1,
            borderColor: 'divider',
            position: 'relative',
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.6rem', color: '#D4D4D8', display: 'block', mb: 0.5 }}
          >
            Before
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.7rem',
              color: hasCurrent ? '#B91C1C' : '#94a3b8',
              fontStyle: hasCurrent ? 'italic' : 'normal',
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {beforeDisplay}
          </Typography>
        </Box>

        {/* After */}
        <Box sx={{ bgcolor: '#F0FDF4', px: 1.5, py: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.6rem', color: '#D4D4D8', display: 'block', mb: 0.5 }}
          >
            After
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.7rem',
              color: sourceVal ? '#065F46' : '#94a3b8',
              fontWeight: sourceVal ? 600 : 400,
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {afterDisplay}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
